import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import {
  INBOX_EVENTS_NAMESPACE,
  listInboxEvents,
  markInboxEventsRead,
  recordInboxFeedReadMarker,
  unreadInboxEventCount,
  type InboxEvent,
} from '../lib/inbox/events';

interface InboxFeedQueryResult {
  events: InboxEvent[];
  unreadCount: number;
}

export function useInboxFeed() {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const queryClient = useQueryClient();
  const userId = wallet?.address ?? 'anonymous';

  const query = useQuery({
    queryKey: ['inbox-events', userId, isReady],
    enabled: isReady,
    queryFn: async (): Promise<InboxFeedQueryResult> => {
      if (!store || !isReady) return { events: [], unreadCount: 0 };
      const events = await listInboxEvents(store, userId);
      const unreadCount = unreadInboxEventCount(events);
      recordInboxFeedReadMarker({ eventCount: events.length, unreadCount });
      return { events, unreadCount };
    },
    staleTime: 1000 * 30,
  });

  const markAllRead = useCallback(async () => {
    if (!store || !isReady) return;
    await markInboxEventsRead(store, userId);
    queryClient.invalidateQueries({ queryKey: ['inbox-events'] });
  }, [store, isReady, queryClient, userId]);

  return {
    events: query.data?.events ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading || !isReady,
    error: query.error as Error | null,
    markAllRead,
    namespace: INBOX_EVENTS_NAMESPACE,
    refetch: query.refetch,
  };
}
